/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import { View, Text, StyleSheet, Platform, ViewStyle } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import FontFamily from "../../../constants/fonts";
import { TOUCHPOINT_DISPLAY_NAMES } from "../../../constants/touchpoints";
import { useTheme } from "../../../theme";
// Cross-platform import for VictoryPie and VictoryContainer
let VictoryPie, VictoryContainer;
if (Platform.OS === "web") {
  // @ts-ignore
  ({ VictoryPie, VictoryContainer } = require("victory"));
} else {
  // @ts-ignore
  ({ VictoryPie, VictoryContainer } = require("victory-native"));
}

interface ProbationaryReviewCardProps {
  days?: number; // Legacy support
  milestoneNumber?: number; // New milestone support
  width?: ViewStyle["width"];
  dueDate?: string;
  status?: "pending" | "completed" | "overdue";
  yesCount?: number;
  noCount?: number;
  naCount?: number;
}

const ProbationaryReviewCard: React.FC<ProbationaryReviewCardProps> = ({
  days,
  milestoneNumber,
  width = "100%",
  dueDate = "Not scheduled",
  status = "pending",
  yesCount = 0,
  noCount = 0,
  naCount = 0,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  // Determine the title based on props
  const getTitle = (): string => {
    if (milestoneNumber) {
      switch (milestoneNumber) {
        case 1:
          return `Probationary Review - ${TOUCHPOINT_DISPLAY_NAMES.MILESTONE_1}`;
        case 2:
          return `Probationary Review - ${TOUCHPOINT_DISPLAY_NAMES.MILESTONE_2}`;
        case 3:
          return `Probationary Review - ${TOUCHPOINT_DISPLAY_NAMES.MILESTONE_3}`;
        default:
          return `Probationary Review - Milestone ${milestoneNumber}`;
      }
    }
    // Legacy support
    if (days) {
      return `Probationary Review - ${days} Days`;
    }
    return "Probationary Review";
  };
  // Ensure we're working with numbers
  const yes = Number(yesCount) || 0;
  const no = Number(noCount) || 0;
  const na = Number(naCount) || 0;

  const total = yes + no + na;

  // Create pie chart data array with percentages
  const pieData = [
    {
      x: "Yes",
      y: yes,
      percentage: total > 0 ? Math.round((yes / total) * 100) : 0,
    },
    {
      x: "No",
      y: no,
      percentage: total > 0 ? Math.round((no / total) * 100) : 0,
    },
    {
      x: "N/A",
      y: na,
      percentage: total > 0 ? Math.round((na / total) * 100) : 0,
    },
  ];

  // Filter out zero values only for the chart display
  const chartData = pieData.filter((item) => item.y > 0);

  // Enhanced colors with better contrast
  const colorScale = ["#33ABDA", "#B05788", "#0078A7"]; // Green, Red, Gray

  return (
    <View
      style={[
        styles.container,
        { width } as ViewStyle,
        isDarkTheme && {
          backgroundColor: colors.background.surface,
          borderColor: colors.border.default,
        },
      ]}
    >
      <View
        style={[
          styles.header,
          isDarkTheme && { backgroundColor: colors.brand.primary },
        ]}
      >
        <MaterialIcons
          name="assignment"
          size={20}
          color="#fff"
          style={styles.headerIcon}
          testID="header-icon"
        />
        <Text style={styles.headerText}>{getTitle()}</Text>
      </View>

      <View
        style={[
          styles.content,
          isDarkTheme && { backgroundColor: colors.background.surface },
        ]}
      >
        <View style={styles.graphContainer}>
          <View
            style={[
              styles.chartWrapper,
              {
                backgroundColor: "transparent",
                elevation: 0,
                shadowColor: "transparent",
              },
            ]}
          >
            <VictoryPie
              data={chartData}
              colorScale={colorScale.slice(0, chartData.length)}
              width={180}
              height={180}
              padding={30}
              innerRadius={80}
              cornerRadius={0}
              startAngle={-6}
              labels={() => null}
              animate={{
                duration: 800,
                easing: "bounce",
                onLoad: { duration: 500 },
              }}
              style={{
                data: {
                  fillOpacity: 0.95,
                  stroke: "#fff",
                  strokeWidth: 2,
                },
              }}
              containerComponent={
                <VictoryContainer
                  responsive={false}
                  style={{
                    touchAction: "auto",
                    userSelect: "auto",
                  }}
                />
              }
            />
            {/* <View style={styles.totalCount}>
              <Text style={styles.totalCountText}>Total</Text>
              <Text style={styles.totalCountNumber}>{total}</Text>
            </View> */}
          </View>
          <View style={styles.legendContainer}>
            {pieData.map((item, index) => (
              <View key={item.x} style={styles.legendItem}>
                <View
                  style={[
                    styles.legendColor,
                    {
                      backgroundColor: colorScale[index], // Always use color, even if value is 0
                      opacity: 1,
                      elevation: 2,
                      shadowColor: "#000",
                      shadowOffset: { width: 0, height: 1 },
                      shadowOpacity: 0.2,
                      shadowRadius: 1,
                    },
                  ]}
                />
                <Text
                  style={[
                    styles.legendText,
                    isDarkTheme
                      ? { color: colors.text.primary }
                      : { color: colors.text.secondary },
                  ]}
                >
                  {item.x}: {item.y} ({item.percentage}%)
                </Text>
              </View>
            ))}
             <View style={styles.totalCount}>
              <Text
                style={[
                  styles.totalCountText,
                  isDarkTheme
                    ? { color: colors.text.primary }
                    : { color: colors.text.secondary },
                ]}
              >
                Total:
              </Text>
              <Text
                style={[
                  styles.totalCountNumber,
                  isDarkTheme
                    ? { color: colors.text.primary }
                    : { color: colors.text.secondary },
                ]}
              >
                {total}
              </Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "#e9ecef",
    elevation: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  header: {
    backgroundColor: "#034895",
    paddingVertical: 12,
    paddingHorizontal: 16,
    flexDirection: "row",
    alignItems: "center",
  },
  headerIcon: {
    marginRight: 8,
  },
  headerText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
  content: {
    padding: 20,
  },
  graphContainer: {
    alignItems: "center",
    marginVertical: 8,
    minHeight: 240,
  },
  chartWrapper: {
    position: "relative",
    width: 180,
    height: 180,
    backgroundColor: "transparent",
    borderRadius: 90,
    elevation: 0,
    shadowColor: "transparent",
  },
  totalCount: {
    // position: "absolute",
    // // top: "50%",
    // // left: "57%",
    // transform: [{ translateX: -30 }, { translateY: -22 }],
    // alignItems: "center",
    // backgroundColor: "rgba(255, 255, 255, 0.9)",
    // padding: 6,
    // borderRadius: 6,

    flexDirection: "row",
    alignItems: "center",
    //backgroundColor: "#f8f9fa",
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
  },
   totalCountText: {
     fontSize: 14,
     //color: "#666",
     fontWeight: "600",
     color: "#FFFFFF",
   },
   totalCountNumber: {
     fontSize: 14,
     fontWeight: "600",
     paddingLeft: 6,
     color: "#FFFFFF",
   },
   legendContainer: {
     flexDirection: "row",
     justifyContent: "center",
     flexWrap: "wrap",
     marginTop: 16,
     gap: 12,
   },
   legendItem: {
     flexDirection: "row",
     alignItems: "center",
     //backgroundColor: "#f8f9fa",
     paddingVertical: 4,
     paddingHorizontal: 8,
     borderRadius: 6,
   },
   legendColor: {
     width: 12,
     height: 12,
     borderRadius: 6,
     marginRight: 8,
   },
   legendText: {
     fontSize: 14,
     color: "#FFFFFF",
   },
  noDataContainer: {
    padding: 20,
    alignItems: "center",
    minHeight: 280,
    justifyContent: "center",
  },
  noDataText: {
    fontSize: 14,
    color: "#666",
    fontStyle: "italic",
  },
});

export default ProbationaryReviewCard;
